import {
  DataSourceType,
  SupportedResourceTypes,
  Tag,
  Category,
  FilterCodeConfig,
} from '../../util/types';
import SearchService, { FhirSearchResult } from '../SearchService';
import { useCallback, useMemo, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useSelector } from 'react-redux';
import { projectPickerSelectors } from '../../../../redux/modules/projectPicker';
import { accountSelectors } from '../../../../redux/modules/account';
import { authSelectors } from '../../../../redux/modules/auth';
import { useActiveProjectCohortId, useFeatures } from '../../../../hooks';
import { configSelectors } from '../../../../redux/modules/config';
import _flatten from 'lodash/flatten';
import _flattenDeep from 'lodash/flattenDeep';

export type TimeFilterConfig = {
  [key in SupportedResourceTypes]?: string;
};

export type DateBounds = {
  min: Date;
  max: Date;
};

export type SearchConfig = {
  key?: string;
  dataSource: DataSourceType;
  dateBounds?: DateBounds;
  useTagFilter?: boolean;
  tagFilters?: Tag[];
  useNotTagFilter?: boolean;
  notTagFilters?: Tag[];
  applyDateFilter?: boolean;
  pageSize?: number;
  useCategoryFilter?: boolean;
  categories?: Category[];
  timeFilterProps?: TimeFilterConfig;
} & FilterCodeConfig &
  (
    | { patientId: string; deviceId: string }
    | { deviceId: undefined; patientId: string }
    | { deviceId: string; patientId: undefined }
  );

const searchService = new SearchService();

const hasMorePatientServiceDataToLoad = (
  searchResult: FhirSearchResult = [],
) => {
  const allResourceSets = _flattenDeep(searchResult);
  const nextLinks = allResourceSets.map(_ => _.nextLink).filter(Boolean);
  return !!nextLinks.length;
};

export type LoadMore = () => Promise<void>;

export type UsePatientViewerFhirSearchArgs = {
  searchConfig: SearchConfig | SearchConfig[];
  dependencies?: any[];
  inMemoryPaginationPageSize?: number;
};

export type UsePatientViewerFhirSearchReturnValue = {
  isLoading: boolean;
  searchResults: FhirSearchResult[];
  loadMore: LoadMore;
  hasMoreToLoad: boolean;
};

type InMemoryPaginationArgs = {
  currentResults: FhirSearchResult[];
  allResults: FhirSearchResult[];
  pageSize?: number;
  useFSSQuery: boolean;
};

type InMemoryPaginationReturnValue = {
  currentResults: FhirSearchResult[];
  allResults: FhirSearchResult[];
};

function shouldDoInMemoryPagination(useFSSQuery: boolean, pageSize?: number) {
  return !!useFSSQuery && !!pageSize;
}

function inMemoryPagination({
  currentResults,
  allResults,
  pageSize,
  useFSSQuery,
}: InMemoryPaginationArgs): InMemoryPaginationReturnValue {
  if (!shouldDoInMemoryPagination(useFSSQuery, pageSize)) {
    return { currentResults: allResults, allResults };
  }

  const copyWithSection = (
    results: FhirSearchResult,
    sliceCount: number,
  ): FhirSearchResult => {
    const copied: FhirSearchResult = [...results].map(_ => ({
      ..._,
      results: [..._.results].slice(0, sliceCount),
    }));
    copied.key = results.key;
    return copied;
  };

  if (!currentResults.length) {
    return {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      currentResults: allResults.map(_ => copyWithSection(_, pageSize)),
      allResults,
    };
  } else {
    const allResourceSets = _flatten(
      currentResults.map(result => result.map(_ => _.results)),
    );
    const pageIndex =
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      Math.ceil(Math.max(...allResourceSets.map(_ => _.length)) / pageSize) - 1;
    const nextResults = allResults.map(_ =>
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      copyWithSection(_, pageSize * (pageIndex + 2)),
    );
    return {
      currentResults: nextResults,
      allResults,
    };
  }
}

const usePatientViewerFhirSearch = ({
  searchConfig,
  dependencies,
  inMemoryPaginationPageSize,
}: UsePatientViewerFhirSearchArgs): UsePatientViewerFhirSearchReturnValue => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<FhirSearchResult[]>([]);
  const [allSearchResults, setAllSearchResults] = useState<FhirSearchResult[]>(
    [],
  );

  const account = useSelector(accountSelectors.selectActiveAccountId);
  const authToken = useSelector(authSelectors.selectAccessToken);
  const config = useSelector(configSelectors.selectConfigState);
  const activeProjectId = useSelector(
    projectPickerSelectors.selectActiveProjectId,
  );
  const cohortId = useActiveProjectCohortId();
  const useFSSQuery = useFeatures()('subjectViewerFSSQueries');

  useDeepCompareEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const auth = { authToken, account, config };
      const arraySearchConfig = Array.isArray(searchConfig)
        ? searchConfig
        : [searchConfig];
      const fhirSearchResults = await Promise.all(
        arraySearchConfig.map(searchConfig =>
          searchConfig
            ? searchService.queryWithSearchConfig(
                // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                auth,
                activeProjectId,
                cohortId,
                searchConfig,
                useFSSQuery,
              )
            : null,
        ),
      );

      const { currentResults, allResults } = inMemoryPagination({
        currentResults: [],
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        allResults: fhirSearchResults,
        pageSize: inMemoryPaginationPageSize,
        useFSSQuery,
      });
      setSearchResults(currentResults);
      setAllSearchResults(allResults);
      setIsLoading(false);
    };

    if (searchConfig && activeProjectId) {
      fetchData();
    }
  }, [
    activeProjectId,
    cohortId,
    account,
    searchConfig || {},
    config,
    ...(dependencies || []),
  ]);

  // load more callback to load more resources for all resource types with a next url
  const loadMore = useCallback(async () => {
    if (!shouldDoInMemoryPagination(useFSSQuery, inMemoryPaginationPageSize)) {
      const auth = { authToken, account, config };
      setIsLoading(true);
      const nextResults = await Promise.all(
        searchResults.map(searchResult =>
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          searchService.loadMore(auth, searchResult),
        ),
      );
      setSearchResults(nextResults);
      setIsLoading(false);
    } else {
      const { currentResults } = inMemoryPagination({
        currentResults: searchResults,
        allResults: allSearchResults,
        pageSize: inMemoryPaginationPageSize,
        useFSSQuery,
      });
      setSearchResults(currentResults);
    }
  }, [
    useFSSQuery,
    inMemoryPaginationPageSize,
    authToken,
    account,
    config,
    searchResults,
    allSearchResults,
  ]);

  const hasMoreToLoad = useMemo(() => {
    if (shouldDoInMemoryPagination(useFSSQuery, inMemoryPaginationPageSize)) {
      return !searchResults.every((searchResult, searchResultIndex) =>
        searchResult.every(({ results }, resourceGroupIndex) => {
          return (
            results.length ===
            allSearchResults?.[searchResultIndex]?.[resourceGroupIndex]?.results
              ?.length
          );
        }),
      );
    } else {
      return searchResults.some(_ => {
        return hasMorePatientServiceDataToLoad(_);
      });
    }
  }, [
    searchResults,
    allSearchResults,
    useFSSQuery,
    inMemoryPaginationPageSize,
  ]);

  return { isLoading, searchResults, loadMore, hasMoreToLoad };
};

export default usePatientViewerFhirSearch;
